import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { LocationPros, EquipmentListProps } from "../../../types/baseTypes";
import { fetchLocationsByService } from "../../../api/locations/locationsClient";
import { fetchLocationEquipment } from "../../../api/locations/locationEquipmentClient";

/**
 * Location Selection Context State
 * 
 * Provides location and equipment data with loading/error states.
 * 
 * @remarks
 * This context is responsible for:
 * - Fetching locations filtered by service
 * - Fetching equipment for selected location
 * - Managing selected location state
 * - Exposing loading and error states
 * 
 * URL state management is handled by `useLocationHydration` hook.
 * 
 * @see {@link useLocationHydration} for URL state hydration
 * @see {@link LocationSelectionProvider} for provider implementation
 */
type LocationSelectionContextState = {
  /** Available locations (undefined = loading, null = loaded but empty) */
  locations: LocationPros[] | null | undefined;
  
  /** Currently selected location */
  selectedLocation: LocationPros | null;
  
  /** Whether locations are being fetched */
  isLoadingLocations: boolean;
  
  /** Whether location fetch failed */
  isErrorLocations: boolean;
  
  /** Error from location fetch (null if no error) */
  errorLocations: unknown | null;
  
  /** Equipment/groups for selected location (null = not loaded or no selection) */
  equipments: EquipmentListProps[] | null | undefined;
  
  /** Whether equipment is being fetched */
  isLoadingEquipments: boolean;
  
  /** Whether equipment fetch failed */
  isErrorEquipments: boolean;
  
  /** Error from equipment fetch (null if no error) */
  errorEquipments: unknown | null;
  
  /**
   * Select a location and trigger equipment fetch.
   * Does not modify URL - use with navigation separately.
   */
  selectLocation: (location: LocationPros) => void;
  
  /**
   * Clear selected location.
   * Does not modify URL - use with navigation separately.
   */
  clearLocation: () => void;
};

const LocationSelectionContext = createContext<LocationSelectionContextState | undefined>(
  undefined
);

/**
 * Location Selection Provider
 * 
 * Provides location and equipment data to child components via context.
 * 
 * @param children - Child components to render
 * @param serviceFilter - Optional service filter (e.g., "keys", "rental")
 * 
 * @example
 * ```tsx
 * <LocationSelectionProvider serviceFilter="keys">
 *   <LocationSelectionContent />
 * </LocationSelectionProvider>
 * ```
 * 
 * @remarks
 * - Fetches locations on mount with optional service filtering
 * - Fetches equipment when location is selected
 * - Does NOT manage URL state (handled by useLocationHydration hook)
 * - Uses TanStack Query for data fetching with 1-minute stale time
 * 
 * @see {@link useLocationSelection} to consume this context
 * @see {@link useLocationHydration} for URL state synchronization
 */
export function LocationSelectionProvider({
  children,
  serviceFilter,
}: {
  children: React.ReactNode;
  serviceFilter?: string;
}) {
  const [selectedLocation, setSelectedLocation] = useState<LocationPros | null>(null);

  // Fetch locations with optional service filter
  const {
    data: locations,
    isLoading: isLoadingLocations,
    isError: isErrorLocations,
    error: errorLocations,
  } = useQuery<LocationPros[]>({
    queryKey: ["locations", serviceFilter ?? "all"],
    queryFn: () => fetchLocationsByService(serviceFilter),
    staleTime: 1000 * 60, // 1 minute
  });

  // Fetch equipment/groups for selected location
  const {
    data: equipments,
    isLoading: isLoadingEquipments,
    isError: isErrorEquipments,
    error: errorEquipments,
  } = useQuery<EquipmentListProps[]>({
    queryKey: ["location-equipment", selectedLocation?.id],
    queryFn: () => fetchLocationEquipment(selectedLocation!.id),
    enabled: !!selectedLocation?.id,
    staleTime: 1000 * 60, // 1 minute
  });

  /**
   * Select a location and trigger equipment fetch.
   * 
   * @remarks
   * This only updates context state. URL updates should be handled
   * separately by the consuming component if needed.
   */
  const selectLocation = useCallback((loc: LocationPros) => {
    setSelectedLocation(loc);
  }, []);

  /**
   * Clear selected location.
   * 
   * @remarks
   * This only updates context state. URL updates should be handled
   * separately by the consuming component if needed.
   */
  const clearLocation = useCallback(() => {
    setSelectedLocation(null);
  }, []);

  const value = useMemo<LocationSelectionContextState>(
    () => ({
      locations: isLoadingLocations ? undefined : locations ?? null,
      selectedLocation,
      isLoadingLocations,
      isErrorLocations,
      errorLocations: errorLocations ?? null,
      
      equipments: selectedLocation ? equipments ?? null : null,
      isLoadingEquipments: !!selectedLocation && isLoadingEquipments,
      isErrorEquipments,
      errorEquipments: errorEquipments ?? null,
      
      selectLocation,
      clearLocation,
    }),
    [
      locations,
      selectedLocation,
      isLoadingLocations,
      isErrorLocations,
      errorLocations,
      equipments,
      isLoadingEquipments,
      isErrorEquipments,
      errorEquipments,
      selectLocation,
      clearLocation,
    ]
  );

  return (
    <LocationSelectionContext.Provider value={value}>
      {children}
    </LocationSelectionContext.Provider>
  );
}

/**
 * Hook to access location selection context.
 * 
 * @returns Location selection context state and actions
 * @throws Error if used outside LocationSelectionProvider
 * 
 * @example
 * ```tsx
 * function LocationList() {
 *   const { locations, selectLocation, isLoadingLocations } = useLocationSelection();
 *   
 *   if (isLoadingLocations) return <Spinner />;
 *   
 *   return (
 *     <ul>
 *       {locations?.map(loc => (
 *         <li key={loc.id} onClick={() => selectLocation(loc)}>
 *           {loc.name}
 *         </li>
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 * 
 * @remarks
 * Must be used within a LocationSelectionProvider.
 * For URL state synchronization, also use useLocationHydration hook.
 */
export function useLocationSelection() {
  const ctx = useContext(LocationSelectionContext);
  if (!ctx) {
    throw new Error(
      "useLocationSelection must be used within a LocationSelectionProvider"
    );
  }
  return ctx;
}
